import { useEffect, useState } from "react"
import { FooterMenu, Loading, PrintTransaction, TableHeader } from "components"
import { FormSection, Journal } from "./components"
import { FormProvider, useForm, useFormContext, useWatch } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import toast from "react-hot-toast"
import { useNavigate, useParams } from "react-router-dom"
import { convertNumber, useApi, useCountryById, useHandleAuth, useProvinceById, useRegencyById, useSubdistrictById } from "utils"
import { useDetail, useInvoiceDetail } from "../ReturnRecord/utils"
import { type FormType, pageConfig, validationSchema, defaultValues } from "./utils"
import moment from "moment"
import { useEmployeeDetail, useSubdomainSetting } from "pages/Purchase/Transaction/PurchaseOrder/utils"
import { useSupplier } from "pages/Purchase/Transaction/components/Supplier/utils"
import { useList } from "pages/Setup/Preference/SettingCompany/utils/hooks"

export default function RefundReturnItem(): JSX.Element {
  const { id } = useParams()
  const { data, isLoading } = useDetail(id)
  const api = useApi()
  const invoice = useInvoiceDetail()
  const navigate = useNavigate()

  const methods = useForm<FormType>({ defaultValues: defaultValues, resolver: yupResolver(validationSchema) })
  const onSubmit = (value: FormType) => {
    const hasInvalidQty = value.refund_return_detail?.some((item) =>
      item.qty_return_item === undefined ||
      item.qty_return_item === null ||
      item.qty_return_item === 0
    )
  
    if (hasInvalidQty) {
      toast.error("Quantity refund cannot be empty or 0")
      return
    }
    if (!value.refund_return_detail || value.refund_return_detail.length === 0) {
      toast.error("No items will be refunded")
      return
    }

    const dataRefund = {
      ...value,
      return_id: id,
      type_refund: pageConfig.type_refund,
      customer_id: data?.datareturn.customer_id,
      location_id: data?.datareturn.location_id,
      address: data?.datareturn.address,
      refund_return_detail: value.refund_return_detail.map((item) => ({
        ...item,
        qty_unit: item.quantity_unit_sell_convention * item.qty_return_item
      }))
    }
    
    return new Promise<void>((resolve) => {
      toast.promise(api.post("/received/addrefundreturn",dataRefund),
        {
          loading: "Loading...",
          success: (res) => res.data.message,
          error: (err) => err.response.data.detail?.message ?? err.response.data.message
        }
      )
      .then(() => navigate(`/sales/transaction/return-record/${id}`))
      .catch(() => {})
      .finally(() => resolve())
    })
  }

  useEffect(() => {
    if (data?.datareturn) {
      invoice.fetchData(data.datareturn.invoice_id)
    }

    // eslint-disable-next-line
  }, [data?.datareturn])

  return (
    <div className="container my-5 flex flex-col gap-3">
      {isLoading || !data ? <Loading errorText="No data available" loading={isLoading} /> : (
        <FormProvider {...methods}>
          <FormSection
            return={{
              address: data.datareturn.address,
              customer_id: data.datareturn.customer_id,
              customer_name: data.datareturn.customer_name,
              detailreturn: data.detailreturn,
              invoice_id: data.datareturn.invoice_id,
              location_id: data.datareturn.location_id,
              location_name: data.datareturn.location_name,
              return_status: data.datareturn.return_status,
              transaction_date: data.datareturn.transaction_date,
              trx_code: data.datareturn.trx_code
            }}
            invoice={{
              sales_order_id: invoice.data?.sales_order_id,
              trx_code: invoice.data?.trx_code
            }}
            return_id={Number(id)}
          />

          <FooterMenu
            hasCancelButton
            save={{
              actiontype: "save",
              loading: methods.formState.isSubmitting ? "true" : undefined,
              onClick: methods.handleSubmit(onSubmit)
            }}
            customElement={{
              journal:<Journal/>,
              print: <PrintRefundReturnItem />,
            }}
          />
        </FormProvider>
      )}
    </div>
  )
}

type PrintType = {
  approve_by?: number
  created_by?: number
  total?: number
  label?: string
  trx_code?: string
  withImage?: boolean
  toggle?: () => void
}

export const PrintRefundReturnItem = (props: PrintType) => {
  const { approve_by } = props
  const { control } = useFormContext<FormType>()
  const [trx_code, transaction_date, refund_return_detail, memo] = useWatch({ control, name: ["trx_code", "transaction_date", "refund_return_detail", "memo"] })

  const setting = useSubdomainSetting()
  const approvedBy = useEmployeeDetail(approve_by)
  const company = useList()
  const country = useCountryById()
  const province = useProvinceById()
  const regency = useRegencyById()
  const subdistrict = useSubdistrictById()
  const supplier = useSupplier()
  const { userTokenData } = useHandleAuth()
  const userToken = userTokenData()

  const [address, setAddress] = useState<string>("")

  const sum = (refund_return_detail || []).reduce((acc, item) => {
    const total: number = (item?.price_refund || 0) * (item?.qty_return_item || 0)
    const totalAfterDiscount: number = total - (total * (item?.discount || 0) / 100)
    const tax = totalAfterDiscount * (item?.tax_rate || 0) / 100

    return {
      subtotal: acc.subtotal + totalAfterDiscount,
      tax: acc.tax + tax
    }
  }, { subtotal: 0, tax: 0 })

  const subtotal = sum.subtotal
  const tax = sum.tax
  const total = subtotal + tax

  useEffect(() => {
    company.fetchData().then((res) => {
      setAddress(res.address)

      if (res.subdistrict_id) subdistrict.fetchData(res.subdistrict_id)
      if (res.regency_id) regency.fetchData(res.regency_id)
      if (res.province_id) province.fetchData(res.province_id)
      if (res.country_id) country.fetchData(res.country_id)
    })

    // eslint-disable-next-line
  }, [])

  const getAddress = (): string => {
    let generateAddress = address ?? ""

    if (subdistrict.data) generateAddress += ", " + subdistrict.data.subdistrict_name
    if (regency.data) generateAddress += ", " + regency.data.regency_name
    if (province.data) generateAddress += ", " + province.data.province_name
    if (country.data) generateAddress += ", " + country.data.country_name
    return generateAddress
  }

  return (
    <PrintTransaction loading={company.isLoading || supplier.isLoading || country.isLoading || province.isLoading || subdistrict.isLoading || regency.isLoading}>
      <section className="flex flex-col gap-3">
        <div className="text-center text-2xl font-bold uppercase">
          {userToken?.company_name}
        </div>

        <section className="grid grid-cols-2 items-stretch gap-1">
          <section className="font-bold">
            <table className="table-top">
              <tbody className="font-bold">
                <tr>
                  <td className="pr-2">ADDRESS</td>
                  <td>: {getAddress()}</td>
                </tr>

                <tr>
                  <td>PHONE</td>
                  <td>
                    {" "}
                    : {setting.data?.phone ? setting.data?.phone : "-"}
                  </td>
                </tr>

                <tr>
                  <td>EMAIL</td>
                  <td>
                    {" "}
                    : {setting.data?.email ? setting.data?.email : "-"}
                  </td>
                </tr>

                <tr>
                  <td>WEBSITE</td>
                  <td>
                    : {setting.data?.website ? setting.data?.website : "-"}
                  </td>
                </tr>
              </tbody>
            </table>
          </section>

          <section className="flex flex-col justify-between items-end">
            <div className="text-2xl font-bold text-right">
              REFUND RETURN INTEM
            </div>

            <table>
              <tbody className="font-bold ">
                <tr>
                  <td width={10} className="text-right">
                    DATE :{" "}
                  </td>
                  <td className="whitespace-nowrap text-right">
                    {transaction_date ? moment(transaction_date).format("DD/MM/YYYY") : "-"}
                  </td>
                </tr>

                <tr>
                  <td width={10} className="whitespace-nowrap">
                    REFUND RETURN INTEM NO :{" "}
                  </td>
                  <td className="whitespace-nowrap text-right">
                    {trx_code}
                  </td>
                </tr>
              </tbody>
            </table>
          </section>
        </section>

        <table className="table table-bordered">
          <TableHeader>
            <tr className="bg-black text-white">
              <th>QTY RETURN</th>
              <th>QTY REFUND</th>
              <th>BACK RETURN</th>
              <th>UNIT</th>
              <th>ITEM CODE</th>
              <th>DESCRIPTION</th>
              <th>PRICE</th>
              <th>DISC (%)</th>
              <th>TOTAL</th>
              <th>TAX</th>
            </tr>
          </TableHeader>
          <tbody>
            {refund_return_detail?.map((item) => {
              const total: number = (item?.price_refund || 0) * (item?.qty_return_item || 0)
              const totalAfterDiscount: number = total - (total * (item?.discount || 0) / 100)

              return (
                <tr>
                  <td className="text-right">{convertNumber(item?.qty_back_returned || 0).intoCurrency}</td>
                  <td className="text-right">{convertNumber(item?.qty_return_item || 0).intoCurrency}</td>
                  <td className="text-right">{convertNumber((item?.qty_back_returned || 0) - (item?.qty_return_item || 0)).intoCurrency}</td>
                  <td>{item?.unit_sell_measure_name || "-"}</td>
                  <td className="text-center">{item?.product_barcode || "-"}</td>
                  <td>{item?.product_name || "-"}</td>
                  <td className="text-right">{convertNumber((item?.price_refund || 0)).intoCurrency}</td>
                  <td className="text-right">{convertNumber((item?.discount || 0)).intoCurrency}%</td>
                  <td className="text-right">{convertNumber(totalAfterDiscount).intoCurrency}</td>
                  <td>{item?.tax_name || "-"} ({(item?.tax_rate || 0)}%)</td>
                </tr>
              )
            })}
          </tbody>
        </table>

        <section className="grid grid-cols-5 items-stretch gap-5">
          <section className="col-span-2 flex flex-col gap-3">
            <table className="table table-bordered">
              <tbody>
                <tr className="bg-black text-white font-bold">
                  <td>Additional Notes:</td>
                </tr>

                <tr className="min-h-[300px]">
                  <td>{memo}</td>
                </tr>
              </tbody>
            </table>

            <section>
              <div className="w-full bg-black text-white font-bold">Authorized By</div>
              <div className="h-[100px]" />
              <div>{userToken?.company_name}</div>
              <div>{approvedBy.data?.job_position_name}</div>
            </section>
          </section>
          <div />

          <section className="col-span-2">
            <table className="w-full break-inside-avoid">
              <tbody>
                <SummarySections label="SUBTOTAL" total={subtotal} />
                <SummarySections label="TAX" total={tax} />
                <SummarySections label="TOTAL REFUND" total={total} />
                <SummarySections label="BALANCE" total={total} />
              </tbody>
            </table>
          </section>
        </section>
      </section>
    </PrintTransaction>
  )
}

export const SummarySections = (props: PrintType) => (
  <tr className="font-bold">
    <td>{props?.label || ""}</td>
    <td className="text-right border-b border-double">
      {props?.total ? convertNumber(props?.total).intoCurrency : "0"}
    </td>
  </tr>
)